import { useState, useEffect, useMemo, type FC } from "react";
import { TabbedPanel } from "@uhg-harmony/react";
import { createLogger } from "../../../features/notifications/utils/logger";
import {
  HarmonyCardColorStyle,
  StyledTabbedPanelContent,
} from "../styles/styles";
import "../styles/library.css";
import { TemplateLibraryHeader } from "./TemplateLibraryHeader";
import { StoryWrapper } from "../../ui/wrappers/StoryWrapper";
import { useAppSelector } from "../../../redux/hooks";
import { selectUserRole } from "../../../redux/user/userSelectors";
import { FOLDER_NAMES } from "../../../constants/templateConstants";
import { isReadOnlyUser } from "../../utils/roleHelpers";
import { ApiFolderTabbedPanel } from "../components/panels/ApiFolderTabbedPanel";
import type {
  GetTemplateFolder,
  ApiTemplateFolder,
} from "../../../redux/templates/templatesTypes";

const logger = createLogger("TemplateLibraryPage");

interface TemplateLibraryProps {
  onBack?: () => void;
  onLoadTemplate?: (html: string) => void;
}

// Helper function to transform GetTemplateFolder[] to ApiTemplateFolder[]
// filterDrafts: when true (default), excludes the "Drafts" folder (used for Account/Client panels)
const transformToApiFolders = (
  folders: GetTemplateFolder[],
  filterDrafts = true,
): ApiTemplateFolder[] => {
  return folders
    .filter((folder) => {
      // Filter out Drafts folder if requested
      if (filterDrafts && folder.folderName === FOLDER_NAMES.DRAFTS) {
        return false;
      }
      // Only include folders that have at least one template with a draft version (templateVersionNumber === null)
      return folder.templates.some((template) =>
        template.versions.some((version) => version.templateVersionNumber === null)
      );
    })
    .map((folder) => ({
      folderName:
        !filterDrafts && folder.folderName === FOLDER_NAMES.DRAFTS
          ? FOLDER_NAMES.DEMO_TEMPLATES
          : folder.folderName,
      templates: folder.templates
        .map((template) => ({
          templateId: template.templateId,
          templateName: template.templateName,
          templateState: template.templateState,
          templateCategory: template.templateCategory,
          folderName: template.folderName,
          created_At: template.createdAt,
          updated_At: template.updatedAt,
          versions: template.versions.filter((version) => version.templateVersionNumber !== null).map((version) => ({
            templateVersionId: version.templateVersionId,
            templateVersionNumber: version.templateVersionNumber,
            s3Path: version.s3Path,
            createdBy: version.createdBy,
            lockedBy: version.lockedBy,
            created_At: version.createdAt,
            updated_At: version.updatedAt,
          })),
        }))
        .filter((template) => template.versions.length > 0)
        .sort((a, b) => a.templateName.localeCompare(b.templateName)),
    }))
    .filter((folder) => folder.templates.length > 0)
    .sort((a, b) => {
      // For global templates (filterDrafts=false), pin DemoTemplates (renamed from Drafts) first
      if (!filterDrafts) {
        if (a.folderName === FOLDER_NAMES.DEMO_TEMPLATES) return -1;
        if (b.folderName === FOLDER_NAMES.DEMO_TEMPLATES) return 1;
      }
      return a.folderName.localeCompare(b.folderName);
    });
};

const TemplateLibraryPage: FC<TemplateLibraryProps> = ({
  onBack,
  onLoadTemplate,
}) => {
  const [currentPanel, setCurrentPanel] = useState(0);
  const { loading, error } = useAppSelector((state) => state.templates);
  const userRole = useAppSelector(selectUserRole);
  const accountId = useAppSelector((state) => state.metadata.accountId);
  const clientId = useAppSelector((state) => state.metadata.clientId);

  const dataByCategory = useAppSelector(
    (state) => state.templates.dataByCategory,
  );

  const optumData = dataByCategory?.OPTUM;
  // Prefer PLATFORM cache, fallback to CLIENT cache (which also has platformTemplates)
  const platformData = dataByCategory?.PLATFORM || dataByCategory?.CLIENT;
  const clientData = dataByCategory?.CLIENT;

  // Reset to Global Templates panel when account or client selection changes
  useEffect(() => {
    if (isReadOnlyUser(userRole) || (!accountId && !clientId)) {
      logger.debug("Displaying panel 0: Global Templates", {
        userRole,
        accountId,
        clientId,
      });
      setCurrentPanel(0); // Global Templates
    } else if (accountId && !clientId) {
      logger.debug("Displaying panel 1: Your Account", {
        userRole,
        accountId,
        clientId,
      });
      setCurrentPanel(1); // Your Account
    } else if (accountId && clientId) {
      logger.debug("Displaying panel 2: Client Templates", {
        userRole,
        accountId,
        clientId,
      });
      setCurrentPanel(2); // Client Templates
    }
  }, [userRole, accountId, clientId]);

  const panels = useMemo(() => [
    {
      header: "Global Templates",
      content: (
        <ApiFolderTabbedPanel
          folderGroups={transformToApiFolders(optumData?.globalTemplates ?? [], false)}
          emptyMessage="No global templates available."
          onLoadTemplate={onLoadTemplate}
        />
      ),
    },
    {
      header: "Your Account",
      content: (
        <ApiFolderTabbedPanel
          folderGroups={transformToApiFolders(
            accountId ? (platformData?.platformTemplates ?? []) : [],
          )}
          emptyMessage={
            !accountId
              ? "Please select an account to view platform templates."
              : "No account templates available."
          }
          onLoadTemplate={onLoadTemplate}
        />
      ),
    },
    {
      header: "Client Templates",
      content: (
        <ApiFolderTabbedPanel
          folderGroups={transformToApiFolders(
            accountId && clientId ? (clientData?.clientTemplates ?? []) : [],
          )}
          emptyMessage={
            !accountId
              ? "Please select an account to view client templates."
              : !clientId
                ? "Please select a client to view client templates."
                : "No client templates available."
          }
          onLoadTemplate={onLoadTemplate}
        />
      ),
    },
  ], [optumData, platformData, clientData, accountId, clientId, onLoadTemplate]);

  return (
    <>
      <HarmonyCardColorStyle />

      <StoryWrapper>
        <TemplateLibraryHeader onBack={onBack} />
        <StyledTabbedPanelContent>
          {loading && (
            <div className="template-library-loading">Loading templates…</div>
          )}
          {!loading && error && (
            <div className="template-library-error">
              Error loading templates: {error}
            </div>
          )}
          {!loading && (
            <TabbedPanel
              panels={panels}
              onHeaderPress={setCurrentPanel}
              selectedPanel={currentPanel}
              titlesAs="h3"
            />
          )}
        </StyledTabbedPanelContent>
      </StoryWrapper>
    </>
  );
};

export default TemplateLibraryPage;
